import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { RowMenu } from '../../ui/row-menu';
import { Button } from '../../ui/button';
import JournalEntryEditor from '../../JournalEntryEditor';
import { useJournalContext } from '../../../contexts/JournalContext';
import { useAiDrafts } from '../../../hooks/useAiDrafts';
import type { TranslationKeys } from '../../../i18n/types.js';
import type { JournalDraftStatus } from '../useJournalDrafts.js';

const fallbackT = (key: keyof TranslationKeys): string => key;

type EditorProps = React.ComponentProps<typeof JournalEntryEditor>;
type JournalEntry = EditorProps['entry'];
type PageInfo = EditorProps['pages'][number];
type UpdateHandler = EditorProps['onUpdate'];

export interface JournalEntityViewProps {
  entryId: string;
  level: 'pane' | 'full';
  entry?: JournalEntry;
  entries?: JournalEntry[];
  pages?: PageInfo[];
  isGM?: boolean;
  /** Sends changes upward (parent emits journal:update over WS). Without the prop, falls back to the JournalContext update callback. */
  onUpdate?: (entryId: string, changes: Parameters<UpdateHandler>[0]) => void;
  onReveal?: () => void;
  onPin?: (pinned: boolean) => void;
  /**
   * Studio: клик по чипу упоминания — открыть упомянутую запись в фокус.
   * Полоса строится сканом текста записи по именам других записей (макет:
   * @mention; rich-render в редакторе нет — навигацию даёт полоса).
   */
  onMention?: (entryId: string) => void;
  /**
   * Studio: an edit of this entry not yet confirmed by the server
   * (useJournalDrafts). Refused — shown with «Save again» (onResave); offline —
   * it is sent after the rejoin. Absent — nothing unconfirmed.
   */
  saveStatus?: JournalDraftStatus;
  onResave?: () => void;
  /** Studio (P03): where the note lives and the buttons to move it — built by the host. */
  ownerBar?: React.ReactNode;
  /** F-4: links of the entry, made and removed in Studio (journal:link / journal:unlink). */
  onLink?: (targetId: string, relation: string) => void;
  onUnlink?: (targetId: string, relation: string) => void;
  /** Q-5 / Q-6: a quest's chain line (above) and «Where in the plan» (below) — built by the host. */
  viewExtras?: EditorProps['viewExtras'];
  /** Studio page chrome (mockup): «TYPE name» heading with Edit / Show to players. */
  studioPage?: EditorProps['studioPage'];
  /** A note just created from «＋ заметка в бит»: opens in the edit mode. */
  startEditing?: boolean;
  /** GJ-12: delete the entry (asks first). The host sends journal:delete; absent — no button. */
  onDelete?: () => void;
  t?: (key: keyof TranslationKeys) => string;
}

export function JournalEntityView({
  entryId,
  level,
  entry: entryProp,
  entries: entriesProp,
  pages: pagesProp,
  isGM,
  onUpdate,
  onReveal,
  onPin,
  onMention,
  saveStatus,
  onResave,
  ownerBar,
  onLink,
  onUnlink,
  viewExtras,
  studioPage,
  startEditing,
  onDelete,
  t = fallbackT,
}: JournalEntityViewProps): React.ReactElement | null {
  const journal = useJournalContext();

  const { drafts } = useAiDrafts();
  const hasJournalDraft: boolean = Array.isArray(drafts)
    ? drafts.some(
        (draft: { entityRef?: { kind?: string; id?: string }; status?: string }): boolean =>
          draft.entityRef?.kind === 'journal' &&
          draft.entityRef?.id === entryId &&
          draft.status === 'pending',
      )
    : false;

  const entries: JournalEntry[] = entriesProp ?? journal.entries;
  const entry: JournalEntry | undefined =
    entryProp ?? entries.find((candidate) => candidate.id === entryId);

  if (entry === undefined) {
    return null;
  }

  const pages: PageInfo[] = pagesProp ?? [];
  const resolvedIsGM: boolean = isGM ?? false;

  const contextUpdate: ((entryId: string, changes: Parameters<UpdateHandler>[0]) => void) | undefined =
    (journal as { update?: (entryId: string, changes: Parameters<UpdateHandler>[0]) => void }).update;

  const handleUpdate: UpdateHandler = (changes) => {
    if (onUpdate !== undefined) {
      onUpdate(entryId, changes);
      return;
    }
    contextUpdate?.(entryId, changes);
  };

  const isFull: boolean = level === 'full';

  // Упоминания: имена ДРУГИХ записей в тексте этой (JSON-скан data; имена
  // короче 3 символов — шум, не считаем).
  const mentioned: { id: string; name: string }[] = [];
  if (onMention !== undefined) {
    const haystack = JSON.stringify((entry as { data?: unknown }).data ?? {});
    for (const candidate of entries as { id: string; name?: string }[]) {
      if (candidate.id === entry.id) continue;
      const name = candidate.name ?? '';
      if (name.length < 3) continue;
      if (haystack.includes(name)) {
        mentioned.push({ id: candidate.id, name });
        if (mentioned.length >= 8) break;
      }
    }
  }

  // Sidebar polish П5: no red button on the page — «Удалить…» is in the ⋯ of the record, it asks first. In Studio it
  // stands at the end of the record's header line (group T), not on a line of its own.
  const deleteMenu: React.ReactElement | null =
    onDelete !== undefined ? (
      <RowMenu
        size="md"
        testid="journal-more"
        items={[
          {
            label: `${t('studio.entry.delete')}…`,
            icon: Trash2,
            danger: true,
            testid: 'journal-delete',
            confirm: {
              title: t('studio.entry.deleteTitle').replace('{name}', (entry as { name?: string }).name ?? ''),
              body: t('studio.entry.deleteBody'),
              yes: t('studio.entry.delete'),
            },
            onSelect: (): void => { onDelete(); },
          },
        ]}
      />
    ) : null;

  return (
    <div
      className="journal-entity-view"
      data-level={level}
      style={isFull ? { width: '100%', height: '100%' } : undefined}
    >
      {ownerBar}
      {deleteMenu !== null && studioPage === undefined ? (
        <div className="journal-entity-view__delete flex justify-end px-3 pt-1.5">{deleteMenu}</div>
      ) : null}

      {hasJournalDraft ? (
        <div className="journal-entity-view__draft-badge flex items-center px-3 py-1.5">
          <span className="rounded-full border border-hl/70 bg-surface px-2 py-px text-label font-semibold uppercase tracking-wide text-hl">
            {t('studio.ai.badgeDraft')}
          </span>
        </div>
      ) : null}
      {mentioned.length > 0 ? (
        <div className="journal-entity-view__mentions flex flex-wrap items-center gap-1.5 border-b border-line/60 px-3 py-2">
          <span className="text-label font-semibold uppercase tracking-label text-dim">
            {'@'}
          </span>
          {mentioned.map((m) => (
            <button
              key={m.id}
              type="button"
              className="journal-entity-view__mention rounded-full border border-hl/40 bg-hl/10 px-2 py-px text-pill text-hl hover:bg-hl/20"
              onClick={(): void => onMention?.(m.id)}
            >
              {m.name}
            </button>
          ))}
        </div>
      ) : null}
      {saveStatus?.state === 'failed' ? (
        <div
          className="journal-entity-view__save flex items-center gap-2 border-b border-line/60 px-3 py-1.5 text-meta text-danger"
          role="alert"
          data-testid="journal-save-failed"
        >
          <span className="min-w-0 flex-1">
            {t('studio.beatPanel.prepSaveFailed')}
            {saveStatus.error !== undefined && saveStatus.error !== '' ? `: ${saveStatus.error}` : ''}
          </span>
          {onResave !== undefined ? (
            <button
              type="button"
              className="flex-none rounded-md border border-danger/40 px-2.5 py-1 hover:bg-danger/10"
              data-testid="journal-resave"
              onClick={onResave}
            >
              {t('studio.beatPanel.prepResave')}
            </button>
          ) : null}
        </div>
      ) : saveStatus?.state === 'offline' ? (
        <p className="journal-entity-view__save border-b border-line/60 px-3 py-1.5 text-meta text-dim" data-testid="journal-save-offline">
          {t('studio.beatPanel.prepOffline')}
        </p>
      ) : null}
      <JournalEntryEditor
        entry={entry}
        entries={entries}
        pages={pages}
        isGM={resolvedIsGM}
        onUpdate={handleUpdate}
        onReveal={onReveal ?? (() => {})}
        onPin={onPin ?? (() => {})}
        onLink={onLink}
        onUnlink={onUnlink}
        onNavigateToEntry={onMention}
        viewExtras={viewExtras}
        studioPage={studioPage}
        startEditing={startEditing}
        headerMenu={studioPage !== undefined ? deleteMenu : undefined}
      />
    </div>
  );
}

export default JournalEntityView;
